/** Sends chosen applicants their personal interview booking link from the organization's WhatsApp. */
import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { INTERVIEW_PLACEHOLDERS, type InviteInterviewsResponse } from "@mubin/contracts";
import { ErrorNote, Field, Modal, useSubmit } from "./ui";
import { errorText, send } from "../lib/api";
import { INVITE_TEXT } from "../lib/interview";

export function InviteModal({
  open,
  to,
  onClose,
  onSent,
}: {
  open: boolean;
  to: { id: string; name: string }[];
  onClose: () => void;
  onSent?: (ids: string[]) => void;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ name: string; error: string }[] | null>(null);
  const body = text ?? INVITE_TEXT;
  const close = () => {
    setFailed(null);
    onClose();
  };
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      const res = await send<InviteInterviewsResponse>("POST", "/v1/volunteer-applications/invite", {
        ids: to.map((a) => a.id),
        body,
      });
      await qc.invalidateQueries({ queryKey: ["volunteers"] });
      onSent?.(res.results.filter((r) => r.ok).map((r) => r.id));
      const bad = res.results
        .filter((r) => !r.ok)
        .map((r) => ({ name: to.find((a) => a.id === r.id)?.name ?? "", error: r.error ?? "" }));
      if (bad.length) setFailed(bad);
      else close();
    }, errorText);
  }
  return (
    <Modal title={`رابط حجز المقابلة إلى ${to.length} من المتقدمين`} open={open} onClose={close} wide>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        {failed && (
          <div className="alert" role="alert">
            لم يُرسل الرابط إلى:
            <ul>
              {failed.map((f) => (
                <li key={f.name}>
                  {f.name}: {f.error}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="muted small">
          لكل متقدم رابط خاص به يختار منه موعده، ويعمل ثلاثة أيام أو حتى يحجز. الأوقات من صفحة «مواعيد
          المقابلات». يُرسل من واتساب الجمعية إلى: {to.map((a) => a.name).join("، ")}
        </p>
        <Field
          id="iv-invite"
          label="نص الرسالة"
          hint={`${INTERVIEW_PLACEHOLDERS.name} اسم المتقدم، ${INTERVIEW_PLACEHOLDERS.link} رابطه الخاص (لازم)، ${INTERVIEW_PLACEHOLDERS.until} آخر يوم للرابط`}
        >
          <textarea
            id="iv-invite"
            rows={12}
            value={body}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
          />
        </Field>
        <div className="form-actions">
          <button
            className="btn btn-primary"
            disabled={busy || !body.includes(INTERVIEW_PLACEHOLDERS.link) || !to.length}
          >
            {busy ? "جارٍ الإرسال…" : `إرسال إلى ${to.length}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
